import React, { useState, useEffect } from "react";
import axios from "axios";

const FileUpload = ({ chooseImgUrl }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileSelectedHandler = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setIsUploadComplete(false);
      setUploadProgress(0);
    }
  };

  useEffect(() => {
    const uploadFile = async () => {
      if (selectedFile) {
        setIsUploading(true);
        setError(null);
        setUploadProgress(0);

        const fd = new FormData();
        fd.append("image", selectedFile, selectedFile.name);

        try {
          const response = await axios.post(
            "https://api.imgbb.com/1/upload?key=ff344b66b85a7175ae885367a3246c55",
            fd,
            {
              onUploadProgress: (progressEvent) => {
                const progress = Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100
                );
                setUploadProgress(progress);
              },
            }
          );

          chooseImgUrl(response.data.data.image.url);
          setIsUploadComplete(true);
        } catch (error) {
          console.error("Upload error:", error);
          setError("Upload failed. Please try again.");
          setUploadProgress(0);
        } finally {
          setIsUploading(false);
        }
      }
    };

    uploadFile();
  }, [selectedFile, chooseImgUrl]);

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setIsUploadComplete(false);
    setError(null);
    chooseImgUrl("");
    // Also reset the file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault(); // This is necessary to allow dropping
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setIsUploadComplete(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="col-span-full sm:col-span-3">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Cover Image
      </label>

      <div
        className="relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          className={`border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
            isUploadComplete
              ? "border-green-300 bg-green-50"
              : isUploading
              ? "border-blue-300 bg-blue-50"
              : error
              ? "border-red-300 bg-red-50"
              : isDragging
              ? "border-blue-400 bg-blue-100"
              : "border-gray-300 bg-gray-50 hover:border-gray-400"
          }`}
        >
          {/* Upload Area */}
          <div className="text-center">
            {!isUploadComplete && !isUploading && (
              <>
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      <span className="text-blue-600 hover:text-blue-500">
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </span>
                  </label>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={fileSelectedHandler}
                    disabled={isUploading}
                  />
                </div>
              </>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-4">
                <div className="mx-auto h-12 w-12 text-blue-600">
                  <svg className="animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">
                    Uploading image...
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500">
                    {uploadProgress}% complete
                  </p>
                </div>
              </div>
            )}

            {/* Upload Complete */}
            {isUploadComplete && (
              <div className="space-y-4">
                <div className="mx-auto h-12 w-12 text-green-600">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Image uploaded successfully!
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Your image is ready to use
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="space-y-4">
                <div className="mx-auto h-12 w-12 text-red-600">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-900">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-center space-x-3">
            {isUploadComplete && (
              <button
                onClick={resetUpload}
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Change Image
              </button>
            )}

            {error && (
              <button
                onClick={resetUpload}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>

          {/* File Info */}
          {selectedFile && !isUploadComplete && !isUploading && (
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-8 w-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
